import { z } from 'zod';
import { isValidCPF } from '@/shared/utils/cpfs';
import { CategoriaDefensorEnum } from '@/modules/people/enums/categoria-defensor.enum';
import { normalizePersonFormFromApi, profileHasPresence } from '@/modules/people/presentation/person-form.utils';
import { telefoneFormSchema } from '@/shared/utils/formZod.schema.utils';

export type PersonTipo = 'todos' | 'defensor' | 'estagiario' | 'colaborador' | 'usuario';

/** Espelha o que o formulário envia em `edit.tsx` (perfis ocultos não entram no payload). */
export interface PersonFormValidationContext {
  tipo: PersonTipo;
  podeVerUsuarios: boolean;
  podeVerColaborador: boolean;
}

/** API pode retornar `null` em edição; normaliza para string antes da validação. */
function nullToEmpty(val: unknown): string {
  return val == null ? '' : String(val);
}

/** `z.object().optional()` não aceita `null` — perfis ausentes na API vêm como `null`. */
function nullToUndefined<T>(val: T | null | undefined): T | undefined {
  return val == null ? undefined : val;
}

function optionalProfileObject<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess(nullToUndefined, schema.optional());
}

const optionalNullableStringSchema = z.preprocess(nullToEmpty, z.string().optional());

const optionalBooleanSchema = z.preprocess(
  (val) => (val == null ? undefined : val),
  z.boolean().optional(),
);

const optionalReferenceIdSchema = z.preprocess(
  (val) => (val === '' || val == null ? undefined : Number(val)),
  z.number().optional(),
);

const optionalEmailSchema = z.preprocess(
  nullToEmpty,
  z
    .string()
    .trim()
    .refine((v) => !v || z.string().email().safeParse(v).success, 'E-mail inválido'),
);

/** Obrigatório no cadastro (campo com * na aba Dados Pessoais). */
const requiredEmailSchema = z.preprocess(
  nullToEmpty,
  z
    .string()
    .trim()
    .min(1, 'Informe o e-mail institucional')
    .email('E-mail institucional inválido'),
);

/** Perfil de usuário entra no escopo da validação (existe no banco ou está ativo). */
function shouldValidateUsuario(
  ctx: PersonFormValidationContext,
  usuario?: { id?: number; ativo?: boolean },
): boolean {
  if (!ctx.podeVerUsuarios) return false;
  return profileHasPresence(usuario);
}

/** Perfil de colaborador entra no escopo da validação (existe no banco ou está ativo). */
function shouldValidateColaborador(
  ctx: PersonFormValidationContext,
  colaborador?: { id?: number; ativo?: boolean },
): boolean {
  if (!ctx.podeVerColaborador) return false;
  if (ctx.tipo !== 'colaborador' && ctx.tipo !== 'todos') return false;
  return profileHasPresence(colaborador);
}

function isTipoVinculoInformado(tipo: unknown): boolean {
  if (tipo === '' || tipo === null || tipo === undefined) return false;
  const n = Number(tipo);
  return !Number.isNaN(n) && n > 0;
}

function validateActiveUsuario(
  data: {
    usuario?: {
      ativo?: boolean;
      id?: number;
      senha?: string;
      loginSenhaHabilitado?: boolean;
      loginTokenHabilitado?: boolean;
    };
  },
  ctx: z.RefinementCtx,
) {
  if (!data.usuario?.ativo) return;

  const loginSenhaHabilitado = data.usuario.loginSenhaHabilitado === true;
  const loginTokenHabilitado = data.usuario.loginTokenHabilitado === true;

  if (!loginSenhaHabilitado && !loginTokenHabilitado) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Selecione ao menos uma forma de login',
      path: ['usuario', 'loginSenhaHabilitado'],
    });
  }

  if (loginSenhaHabilitado && !data.usuario.id && !String(data.usuario.senha ?? '').trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Informe a senha', path: ['usuario', 'senha'] });
  }
}

function validateActiveColaborador(
  data: { colaborador?: { ativo?: boolean; tipo_vinculo?: unknown } },
  ctx: z.RefinementCtx,
) {
  if (!data.colaborador?.ativo) return;
  if (!isTipoVinculoInformado(data.colaborador.tipo_vinculo)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Selecione o tipo de vínculo',
      path: ['colaborador', 'tipo_vinculo'],
    });
  }
}

const personFormBaseSchema = z
  .object({
    nome: z.string().trim().min(1, 'Informe o nome completo'),
    cpf: z
      .string()
      .trim()
      .min(1, 'Informe o CPF')
      .refine((v) => isValidCPF(v), 'CPF inválido'),
    dataNascimento: optionalNullableStringSchema,
    estadocivil: z.coerce.number().optional(),
    email: optionalEmailSchema,
    emailInstitucional: requiredEmailSchema,
    podeserresponsavel: optionalBooleanSchema,
    endereco: z
      .object({
        cep: optionalNullableStringSchema,
        logradouro: optionalNullableStringSchema,
        numero: z.union([z.string(), z.number()]).optional(),
        complemento: optionalNullableStringSchema,
        bairro: optionalNullableStringSchema,
        cidade: optionalNullableStringSchema,
        estado: optionalNullableStringSchema,
      })
      .optional(),
    telefone: telefoneFormSchema,
    celular: telefoneFormSchema,
    usuario: optionalProfileObject(
      z
        .object({
          id: z.coerce.number().optional(),
          ativo: optionalBooleanSchema,
          senha: optionalNullableStringSchema,
          visao: z.union([z.string(), z.number()]).optional(),
          corfundotabela: optionalNullableStringSchema,
          corletratabela: optionalNullableStringSchema,
          recebenotificacoes: optionalBooleanSchema,
          loginSenhaHabilitado: optionalBooleanSchema,
          loginTokenHabilitado: optionalBooleanSchema,
        })
        .passthrough(),
    ),
    colaborador: optionalProfileObject(
      z
        .object({
          id: z.coerce.number().optional(),
          ativo: optionalBooleanSchema,
          tipo_vinculo: z.union([z.string(), z.number()]).optional(),
          orgao_origem: optionalNullableStringSchema,
          matricula: optionalNullableStringSchema,
          vinculo_direto: optionalBooleanSchema,
          cessao_onerosa: optionalBooleanSchema,
          anotacao: optionalNullableStringSchema,
          data_cessao: optionalNullableStringSchema,
          data_saida: optionalNullableStringSchema,
          funcao: z
            .object({
              id: optionalReferenceIdSchema,
            })
            .optional(),
        })
        .passthrough(),
    ),
    defensor: optionalProfileObject(
      z
        .object({
          id: z.coerce.number().optional(),
          ativo: optionalBooleanSchema,
          categoria: z.preprocess(
            (val) => (val === '' || val == null ? null : val),
            z.nativeEnum(CategoriaDefensorEnum).nullable().optional(),
          ),
        })
        .passthrough(),
    ),
    estagiario: optionalProfileObject(
      z
        .object({
          id: z.coerce.number().optional(),
          ativo: optionalBooleanSchema,
          anotacao: optionalNullableStringSchema,
        })
        /** Preserva `estagios` e demais campos enviados ao salvar. */
        .passthrough(),
    ),
  })
  .passthrough();

/** Validação alinhada ao cadastro: mesmos campos * e perfis enviados conforme `tipo`/permissões. */
export function buildPersonFormSchema(context: PersonFormValidationContext) {
  const validated = personFormBaseSchema.superRefine((data, ctx) => {
    const usuario = data.usuario ?? undefined;
    const colaborador = data.colaborador ?? undefined;
    // Campos obrigatórios do perfil só quando ativo; inativo com `id` permanece no formulário sem exigir login/papel/vínculo.
    if (shouldValidateUsuario(context, usuario) && usuario?.ativo) {
      validateActiveUsuario({ ...data, usuario }, ctx);
    }
    if (shouldValidateColaborador(context, colaborador) && colaborador?.ativo) {
      validateActiveColaborador({ ...data, colaborador }, ctx);
    }
  });

  return z.preprocess(
    (val) => (val != null && typeof val === 'object' ? normalizePersonFormFromApi(val as never) : val),
    validated,
  );
}

export const personFormSchema = buildPersonFormSchema({
  tipo: 'todos',
  podeVerUsuarios: true,
  podeVerColaborador: true,
});

export type PersonFormValues = z.infer<typeof personFormBaseSchema>;
